'use client';
import { useEffect, useState } from 'react';

/**
 * Тема дашборда. Дефолт — СВЕТЛАЯ (борд + лендинг), тёмная включается
 * атрибутом `data-theme="dark"` на <html>; значения токенов живут в
 * src/styles/tokens.css, здесь только переключатель.
 *
 * Акцент больше НЕ подставляется в рантайме: раньше хук писал
 * `--c-accent` из localStorage, из-за чего бренд-цвет мог разъехаться с
 * системой. Брендирование организации — отдельный хук useOrgBrand.
 */
const THEME_KEY = 'logixa_theme';
const ACCENT_KEY = 'logixa_accent';

export type Theme = 'dark' | 'light';

export function applyTheme(t: Theme) {
  try {
    localStorage.setItem(THEME_KEY, t);
    document.documentElement.setAttribute('data-theme', t);
    window.dispatchEvent(new Event('logixa-theme-change'));
  } catch {/* private mode */}
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>('light');
  // Персональный акцент из настроек. Красит ТОЛЬКО `--brand` (действие),
  // семантические success/warning/danger не трогает. Пусто → системный
  // LOGIXA-оранжевый из tokens.css.
  const [accent, setAccentState] = useState('');

  useEffect(() => {
    let saved: Theme = 'light';
    try { saved = localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'; } catch {}
    setThemeState(saved);
    document.documentElement.setAttribute('data-theme', saved);

    try {
      const savedAccent = localStorage.getItem(ACCENT_KEY) || '';
      if (savedAccent) {
        setAccentState(savedAccent);
        document.documentElement.style.setProperty('--brand', savedAccent);
      }
    } catch {/* private mode */}

    const sync = () => {
      try { setThemeState(localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'); } catch {}
    };
    window.addEventListener('logixa-theme-change', sync);
    return () => window.removeEventListener('logixa-theme-change', sync);
  }, []);

  const setTheme = (t: Theme) => { setThemeState(t); applyTheme(t); };
  const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  const setAccent = (color: string) => {
    setAccentState(color);
    try {
      localStorage.setItem(ACCENT_KEY, color);
      localStorage.setItem('logixa_accent_personal', '1');   // перебивает бренд организации
    } catch {/* private mode */}
    document.documentElement.style.setProperty('--brand', color);
  };

  return { theme, setTheme, toggle, accent, setAccent };
}
